import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { RefereeCertification, MatchRole, resolveEnum } from '@/models/tournament';
import type { MatchView } from '@/models/tournament';

interface MatchDetailsPanelProps {
  matches: MatchView[];
  selectedMatch: MatchView | null;
  onSelectMatch: (match: MatchView) => void;
}

const MatchDetailsPanel: React.FC<MatchDetailsPanelProps> = ({ matches, selectedMatch, onSelectMatch }) => {
  const { t, i18n } = useTranslation('tournament');

  const certificationKeys: Record<RefereeCertification, string> = {
    [RefereeCertification.NationalLevelA]: 'National Level A',
    [RefereeCertification.AFCElite]: 'AFC Elite',
    [RefereeCertification.FIFAInternational]: 'FIFA International',
  };

  const roleKeys: Record<MatchRole, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const getStatusColor = (match: MatchView): string => {
    if (match.refereeTeamMembers?.length > 0) {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (match: MatchView): string => {
    const count = match.refereeTeamMembers?.length ?? 0;
    return count > 0 ? t('Assigned ({{count}})', { count }) : t('Unassigned');
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Match Selection */}
      <div className="bg-card border border-border rounded-lg p-4" data-testid="match-selection-panel">
        <h2 className="text-lg font-semibold text-foreground flex items-center mb-4">
          <Icon name="Calendar" size={20} className="me-2 text-primary" />
          {t('Select Match to Assign')}
        </h2>

        <div className="space-y-3 max-h-[500px] overflow-y-auto pe-2">
          {matches.map((match) => (
            <div
              key={match.id}
              onClick={() => onSelectMatch(match)}
              className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                selectedMatch?.id === match.id
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border hover:border-primary/50 hover:shadow-sm'
              }`}
              data-testid={`match-select-card-${match.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">{match.tournamentName}</p>
                  <p className="text-sm font-semibold text-foreground">
                    {match.team1Name} <span className="text-muted-foreground/70">{t('vs')}</span> {match.team2Name}
                  </p>
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  getStatusColor(match)
                }`}>
                  {getStatusLabel(match)}
                </span>
              </div>

              <div className="flex items-center text-xs text-muted-foreground space-x-4 rtl:space-x-reverse mb-2">
                <div className="flex items-center">
                  <Icon name="Calendar" size={12} className="me-1 text-gray-400" />
                  <span>{formatDate(match.scheduledDateTime)}</span>
                </div>
                <div className="flex items-center">
                  <Icon name="Clock" size={12} className="me-1 text-gray-400" />
                  <span>{formatTime(match.scheduledDateTime)}</span>
                </div>
              </div>

              <div className="flex items-center text-xs text-muted-foreground">
                <Icon name="MapPin" size={12} className="me-1 text-gray-400" />
                <span className="truncate">{match.location}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Match Details */}
      {selectedMatch && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="match-details-panel">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="FileText" size={20} className="me-2 text-primary" />
            {t('Match Details')}
          </h3>

          {/* Match Info */}
          <div className="bg-gradient-to-r from-primary/10 to-blue-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-center space-x-6 rtl:space-x-reverse mb-3">
              <div className="text-center">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-2 mx-auto shadow-sm">
                  <Icon name="Shield" size={24} className="text-primary" />
                </div>
                <p className="text-sm font-bold text-foreground">{selectedMatch.team1Name}</p>
              </div>
              <div className="text-xl font-bold text-muted-foreground/70">{t('VS')}</div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-2 mx-auto shadow-sm">
                  <Icon name="Shield" size={24} className="text-blue-600" />
                </div>
                <p className="text-sm font-bold text-foreground">{selectedMatch.team2Name}</p>
              </div>
            </div>
            <p className="text-xs text-center text-muted-foreground">{selectedMatch.tournamentName}</p>
          </div>

          {/* Date, Time, Venue */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center mb-1">
                <Icon name="Calendar" size={14} className="text-muted-foreground me-2" />
                <p className="text-xs font-medium text-foreground">{t('Date')}</p>
              </div>
              <p className="text-sm text-foreground">{formatDate(selectedMatch.scheduledDateTime)}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center mb-1">
                <Icon name="Clock" size={14} className="text-muted-foreground me-2" />
                <p className="text-xs font-medium text-foreground">{t('Time')}</p>
              </div>
              <p className="text-sm text-foreground">{formatTime(selectedMatch.scheduledDateTime)}</p>
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-3 mb-4">
            <div className="flex items-center mb-1">
              <Icon name="MapPin" size={14} className="text-muted-foreground me-2" />
              <p className="text-xs font-medium text-foreground">{t('Venue')}</p>
            </div>
            <p className="text-sm text-foreground">{selectedMatch.location}</p>
          </div>

          {/* Required Certification */}
          {selectedMatch.requiredCertification !== undefined && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-center">
                <Icon name="Award" size={14} className="text-blue-600 me-2" />
                <div>
                  <p className="text-xs text-blue-700">{t('Required Certification')}</p>
                  <p className="text-sm font-semibold text-blue-900">
                    {t(certificationKeys[selectedMatch.requiredCertification] ?? 'Unknown')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Assignment Status */}
          {selectedMatch.refereeTeamMembers?.length > 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Icon name="Users" size={14} className="text-green-600 me-2" />
                  <p className="text-xs text-green-700">{t('Referee Team Members')}</p>
                </div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <Icon name="CheckCircle" size={12} className="me-1" />
                  {t('Assigned ({{count}})', { count: selectedMatch.refereeTeamMembers.length })}
                </span>
              </div>
              <div className="space-y-1.5">
                {selectedMatch.refereeTeamMembers.map((member) => (
                  <div key={member.id} className="flex items-center text-sm">
                    <Icon name="User" size={12} className="text-green-600 me-2" />
                    <span className="font-medium text-green-900">{member.userName}</span>
                    <span className="text-green-700 ms-1.5 text-xs">({t(roleKeys[(resolveEnum(MatchRole, member.role) as MatchRole) ?? member.role] ?? 'Unknown')})</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-muted/50 border border-border rounded-lg p-3 mb-4">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={14} className="text-muted-foreground me-2" />
                <div>
                  <p className="text-xs text-foreground">{t('Assignment Status')}</p>
                  <p className="text-sm font-semibold text-foreground">{t('No referee assigned')}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatchDetailsPanel;
